import { BaseAction } from "../../../core/actions/BaseAction";
import { _eventBus } from "../../../core/events/EventBus";
import { calculateTrainAndShuttlePricesFromBooking, updatePaymentItemsForCancelBooking } from "../../../Lib/Utils/validationUtil";
import { getInfantGuardianMap, getLinkedInfantIds } from "../../../Lib/Utils/infantLinking";
import { cancelBookingTree } from "../../../Orchestrator/ConstantTree/CancelBookingPage/CancelBookingTree";
import { getRecoilStateValue, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { TrainTypeEnum, TripTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { IBookingResponse, ISelectedPassengerForCancelIds, PassengerPriceDetails } from "../../../Orchestrator/Types/Index";


export class PassengerOutboundCheckAction extends BaseAction<void, {
    checked: boolean,
    passengerId: string,
    selectedPassengerForCancel: ISelectedPassengerForCancelIds[],
    allPassengerPriceAndAgeGroup: PassengerPriceDetails[],
    allPassengerPriceAndAgeGroupInBound: PassengerPriceDetails[],
    tripDisableForCancelSelection: string[],
    // Optional: several TrainInfoCard* auto-select emitters omit it; the
    // action falls back to bookingResponseAtom.
    booking?: IBookingResponse,
    outboundChecked: boolean,
    inboundChecked: boolean
}> {
    async run(): Promise<void | null> {
        try {

            // ponytail: default to [] ("nothing disabled") — TrainInfoCardForCancel emits this event without tripDisableForCancelSelection, which crashed .includes(). Guarding the shared consumer covers all emitters.
            const { checked, passengerId, selectedPassengerForCancel, allPassengerPriceAndAgeGroup, allPassengerPriceAndAgeGroupInBound, tripDisableForCancelSelection = [], booking: bookingParam, outboundChecked, inboundChecked } = this.params!;
            // Same emitter also omits `booking`; computing prices from undefined
            // returned [] and silently wiped paymentOutBoundItemsDetailsAtom
            // (refund summary lost its fare lines + total). Fall back to the
            // atom the payload is a snapshot of; without a booking, skip the
            // rewrite entirely rather than write empty.
            const booking = bookingParam ?? getRecoilStateValue<IBookingResponse>("bookingResponseAtom");
            let mainData: any = [];
            let filteredOutboundData: any = [];
            let filteredInboundData: any = [];
            // Infant–adult linking: the carrying adult and lap infant are ONE
            // inseparable unit for every after-sales action, cancel included
            // (backend cannot modify a booking whose linked infant was
            // cancelled alone). Toggling either member toggles the whole
            // linked group, in both directions.
            const guardianId = getInfantGuardianMap(booking).get(passengerId);
            const anchorId = guardianId ?? passengerId;
            const linkedGroup = [anchorId, ...getLinkedInfantIds(booking, anchorId)];
            // A linked member already REFUNDED (e.g. an infant cancelled
            // earlier) is not travelling: never ADD it — it must not reach the
            // refund-price calculation. Unchecking still sweeps the full group.
            const isActive = (id: string) =>
                allPassengerPriceAndAgeGroup?.some((d) => d.pId === id && d.pStatus === "FULFILLED") ||
                allPassengerPriceAndAgeGroupInBound?.some((d) => d.pId === id && d.pStatus === "FULFILLED");
            const activeGroup = linkedGroup.filter(isActive);
            if (checked) {
                mainData = [
                    ...selectedPassengerForCancel,
                    ...activeGroup.filter((id) => !selectedPassengerForCancel.some((d: any) => d === id)),
                ];

                if (!tripDisableForCancelSelection.includes(TrainTypeEnum.outbound)) {
                    filteredOutboundData = mainData.filter((pId: string) => {
                        const passenger = allPassengerPriceAndAgeGroup.find(p => p.pId === pId);
                        return passenger?.pStatus !== "REFUNDED";
                    });
                    updateRecoilState("selectedPassengerForCancelAtom", filteredOutboundData);
                }

                if (allPassengerPriceAndAgeGroupInBound && allPassengerPriceAndAgeGroupInBound.length > 0
                    && !tripDisableForCancelSelection.includes(TrainTypeEnum.inbound)
                ) {
                    filteredInboundData = mainData.filter((pId: string) => {
                        const passenger = allPassengerPriceAndAgeGroupInBound.find(p => p.pId === pId);
                        return passenger?.pStatus !== "REFUNDED";
                    });
                    updateRecoilState("selectedPassengerForCancelInBoundAtom", filteredInboundData);
                }

            } else {
                mainData = selectedPassengerForCancel.filter((d: any) => !linkedGroup.includes(d));

                if (!tripDisableForCancelSelection.includes(TrainTypeEnum.outbound)) {
                    filteredOutboundData = mainData.filter((pId: string) => {
                        const passenger = allPassengerPriceAndAgeGroup.find(p => p.pId === pId);
                        return passenger?.pStatus !== "REFUNDED";
                    });
                    updateRecoilState("selectedPassengerForCancelAtom", filteredOutboundData);
                }
                if (allPassengerPriceAndAgeGroupInBound && allPassengerPriceAndAgeGroupInBound.length > 0
                    && !tripDisableForCancelSelection.includes(TrainTypeEnum.inbound)) {
                    filteredInboundData = mainData.filter((pId: string) => {
                        const passenger = allPassengerPriceAndAgeGroupInBound.find(p => p.pId === pId);
                        return passenger?.pStatus !== "REFUNDED";
                    });
                    updateRecoilState("selectedPassengerForCancelInBoundAtom", filteredInboundData);
                }
            }

            if (booking?.booking && !tripDisableForCancelSelection.includes(TrainTypeEnum.outbound)) {
                // Calculate train and shuttle prices from booking data for selected passengers
                const combinedPriceDetails = calculateTrainAndShuttlePricesFromBooking(
                    booking,
                    mainData,
                    TripTypeEnum.OUT_BOUND
                );

                const response = updatePaymentItemsForCancelBooking(combinedPriceDetails, "paymentOutBoundItemsDetailsAtom");
                updateRecoilState("paymentOutBoundItemsDetailsAtom", response);
            }


            if (booking?.booking
                && allPassengerPriceAndAgeGroupInBound && allPassengerPriceAndAgeGroupInBound.length > 0
                && !tripDisableForCancelSelection.includes(TrainTypeEnum.inbound)
            ) {
                // Calculate train and shuttle prices from booking data for selected passengers (inbound)
                const combinedInboundPriceDetails = calculateTrainAndShuttlePricesFromBooking(
                    booking,
                    mainData,
                    TripTypeEnum.IN_BOUND
                );
                
                const response = updatePaymentItemsForCancelBooking(combinedInboundPriceDetails, "paymentInboundItemsDetailsAtom");
                updateRecoilState("paymentInboundItemsDetailsAtom", response);
            }

            _eventBus.emit({
                eventName: cancelBookingTree.CancelBooking.Init,
                data: {
                    booking: booking,
                    filteredOutboundData: filteredOutboundData,
                    filteredInboundData: filteredInboundData,
                    outboundChecked: outboundChecked,
                    inboundChecked: inboundChecked
                }
            });


        } catch (error) {
            console.error("Error in PassengerOutboundCheck action.:", error);
        }
    }
}

export const passengerOutboundCheckAction = new PassengerOutboundCheckAction();